import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { PageWrapper } from '../components/layout/PageWrapper'
import { Seo } from '../components/Seo'
import { fetchBlogList } from '../lib/api/blog'
import { BlogCard } from '../components/blog/BlogCard'
import { categorySlug } from '../lib/blogCategory'

// Page size doubles as the skeleton count so the grid reserves the right height
// and the footer below it doesn't jump when the real cards load (CLS).
const PAGE_SIZE = 12

export function BlogIndex() {
  // Rendered at both /blog and /blog/category/:categorySlug (crawlable archives).
  const { categorySlug: catParam } = useParams<{ categorySlug?: string }>()
  const [page, setPage] = useState(1)
  useEffect(() => { setPage(1) }, [catParam])

  const { data, isLoading } = useQuery({
    queryKey: ['blog', catParam ?? 'all', page],
    queryFn: () => fetchBlogList({ category: catParam, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  })

  const categories = data?.categories ?? []
  const activeCategory = catParam ? categories.find(c => categorySlug(c) === catParam) : undefined
  const pill = (active: boolean) =>
    `px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'}`

  const title = activeCategory
    ? `${activeCategory} — Plumbing Advice & Guides`
    : 'Emergency Plumbing Advice, Guides & DIY Fixes'
  const description = activeCategory
    ? `${activeCategory}: practical plumbing guides from the FixDelivered team — how-tos, DIY fixes, typical costs and when to call a professional.`
    : 'Practical plumbing help from FixDelivered — emergency how-tos, DIY fixes, typical costs and when to call a professional. Burst pipes, boilers, leaks, blocked drains and more.'

  return (
    <PageWrapper>
      <Seo
        title={title}
        description={description}
        path={catParam ? `/blog/category/${catParam}` : '/blog'}
        breadcrumbs={activeCategory
          ? [{ name: 'Guides', url: '/blog' }, { name: activeCategory, url: `/blog/category/${catParam}` }]
          : undefined}
      />
      <div className="max-w-5xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
          {activeCategory ?? 'Plumbing advice & guides'}
        </h1>
        <p className="text-gray-500 mb-8 max-w-2xl">
          {activeCategory
            ? `Our ${activeCategory.toLowerCase()} guides — practical, no-nonsense help from the FixDelivered team.`
            : `Practical, no-nonsense help from the FixDelivered team — how to handle common emergencies, DIY fixes you can do yourself, what jobs typically cost, and when it's time to call a professional.`}
        </p>

        {categories.length > 0 && (
          <nav className="flex flex-wrap gap-2 mb-8" aria-label="Blog categories">
            <Link to="/blog" className={pill(!catParam)}>All</Link>
            {categories.map(c => (
              <Link key={c} to={`/blog/category/${categorySlug(c)}`} className={pill(activeCategory === c)}>{c}</Link>
            ))}
          </nav>
        )}

        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-gray-100 overflow-hidden">
                <div className="aspect-[16/9] bg-gray-100 animate-pulse" />
                <div className="p-5 space-y-2.5">
                  <div className="h-3 w-20 bg-gray-100 animate-pulse rounded" />
                  <div className="h-4 w-3/4 bg-gray-100 animate-pulse rounded" />
                  <div className="h-3 w-full bg-gray-100 animate-pulse rounded" />
                  <div className="h-3 w-16 bg-gray-100 animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : data && data.posts.length > 0 ? (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {data.posts.map(p => <BlogCard key={p.slug} post={p} />)}
            </div>
            {(page > 1 || data.hasMore) && (
              <div className="flex justify-center items-center gap-4 mt-10">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 disabled:opacity-40 hover:border-gray-300"
                >Previous</button>
                <span className="text-sm text-gray-400">Page {page}</span>
                <button
                  disabled={!data.hasMore}
                  onClick={() => setPage(p => p + 1)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 disabled:opacity-40 hover:border-gray-300"
                >Next</button>
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-500">No articles yet — check back soon.</p>
        )}
      </div>
    </PageWrapper>
  )
}
